/* eslint-disable no-prototype-builtins */
import {Model} from '@/models/model';
import TriggerNode from '@/components/workflows/nodes/TriggerNode';
import ActionNode from '@/components/workflows/nodes/ActionNode';
import AssignNode from '@/components/workflows/nodes/AssignNode';
import ConditionNode from '@/components/workflows/nodes/ConditionNode';
import ConditionBranchNode from '@/components/workflows/nodes/ConditionBranchNode';
import EmptyNode from '@/components/workflows/nodes/EmptyNode';
import TriggerSettings from '@/components/workflows/settings/TriggerSettings';
import ActionSettings from '@/components/workflows/settings/ActionSettings';
import AssignSettings from '@/components/workflows/settings/AssignSettings';
import ConditionSettings from '@/components/workflows/settings/ConditionSettings';
import ConditionBranchSettings from '@/components/workflows/settings/ConditionBranchSettings';
import {TRIGGER_TYPES} from '@/models/workflow';
import {defineComponent, reactive, shallowRef} from 'vue';
import OutputsViewer from '@/components/workflows/common/OutputsViewer';
import {Output} from '@/models/output';
import {Type} from '@/models/type';
import {useConfig} from '@/helpers/config-effect';
import ErrorsViewer from '@/components/workflows/common/ErrorsViewer';

const config = useConfig();

const ACTION_COMPONENTS_MAP = {};
const EVENT_COMPONENTS_MAP = {};

export const NODE_TYPES = {
    global: {
        label: 'Globals',
        icon: 'public',
    },
    trigger: {
        label: 'Trigger',
        icon: 'bolt',
    },
    assign: {
        label: 'Assignment',
        icon: 'exit_to_app',
    },
    action: {
        label: 'Action',
        icon: 'play_circle_filled',
    },
};

class NodeOutputsExtractor {
    static trigger(node) {
        if (node.properties.type === 'schedule') {
            return [
                new Output({
                    id: 'time',
                    group: 'trigger',
                    name: 'Time',
                    description: 'The current time when the trigger got fired.',
                    type: Type.integer(),
                    _node: node
                }),
            ];
        }

        if (node.properties.type === 'webhook') {
            return Object.keys(node.properties.arguments.webhook.parameters).map(parameterId => {
                const parameter = node.properties.arguments.webhook.parameters[parameterId];
                return new Output({
                    id: parameterId,
                    group: 'trigger',
                    name: parameter.name || parameterId,
                    description: 'Webhook parameter.',
                    type: parameter.type,
                });
            });
        }

        if (node.properties.type === 'event') {
            const event = config.triggers.events[node.properties.arguments.event?.id];
            return event ? event.outputs.map(output => new Output({...output, group: 'trigger'})) : [];
        }

        return [];
    }

    static action(node) {
        return config.actions[node.properties.id]?.outputs.map(
            output => new Output({
                ...output,
                group: `actions.${node.properties.name?.trim() || node.uid}`,
                _node: node
            })
        ) || [];
    }

    static assign(node) {
        return Object.keys(node.properties?.variables || {}).map(variableId => {
            const variable = node.properties.variables[variableId];

            return new Output({
                id: variableId,
                name: variable.name || variableId,
                group: 'assignments',
                description: `Assigned variable to "${variable.value}".`,
                type: Type.any(),
                _node: node
            });
        });
    }

    static global(node) {
        return Object.keys(config.globals || {}).map(outputId => {
            const output = config.globals[outputId];

            return new Output({
                ...output,
                group: 'globals',
                _node: node
            });
        });
    }
}

class NodeError extends Model {
    static create(id, message, args) {
        return new NodeError({
            id,
            message,
            args,
        });
    }
}

class NodeErrorsBag {
    constructor() {
        this.items = {};
    }

    static create() {
        return new NodeErrorsBag();
    }

    get(id = null) {
        return id === null ? this.items : this.items[id] || [];
    }

    add(id, error) {
        if (!this.items.hasOwnProperty(id)) {
            this.items[id] = [];
        }

        this.items[id].push(error);
    }

    get length() {
        return Object.keys(this.items).length;
    }
}

class NodeErrorsChecker {
    static trigger(node) {
        const errors = NodeErrorsBag.create();

        if (node.properties.type === 'schedule' && !node.properties.arguments.schedule?.frequency) {
            errors.add('schedule-slug', NodeError.create('no-frequency', 'The schedule frequency has not been specified.'));
        }

        if (node.properties.type === 'event' && !node.properties.arguments.event?.id) {
            errors.add('event-name', NodeError.create('no-name', 'The event name has not been specified.'));
        }

        if (node.properties.type === 'event' && node.properties.arguments.event?.id && !config.triggers.events.hasOwnProperty(node.properties.arguments.event?.id)) {
            errors.add('event-name', NodeError.create('no-name', `The event ${node.properties.arguments.event?.id} could not be found, please make sure that all integrations are enabled.`));
        }

        if (node.properties.type === 'event' && node.properties.arguments.event?.id === 'custom' &&
            !node.properties.arguments.event?.customEvent) {
            errors.add('custom-event-name', NodeError.create('no-slug', 'The custom event name has not been specified.'));
        }

        if (node.properties.type === 'webhook' && !node.properties.arguments.webhook?.slug) {
            errors.add('webhook-slug', NodeError.create('no-slug', 'The webhook slug has not been specified.'));
        }

        return errors;
    }

    static action(node) {
        const errors = NodeErrorsBag.create();

        if (node.properties.id && !config.actions[node.properties.id]) {
            errors.add('action', NodeError.create('action-not-found', `The action "${node.properties.id}" could not be found, please make sure that all integrations are enabled.`));
        }

        return errors;
    }
}

export class Node extends Model {
    cast_children(rawChildren, rawAttributes) {
        rawAttributes._workflow.nodesCache[this.uid] = this;

        return rawChildren.map((child, index) => {
            let level = (rawAttributes.level || 0) + (index + 1);

            return new Node({
                ...child,
                level,
                _parentNode: this,
                _workflow: rawAttributes._workflow
            });
        });
    }

    cast_properties(rawProperties) {
        if (rawProperties.arguments?.webhook?.parameters) {
            for (const parameterId in rawProperties.arguments.webhook.parameters) {
                rawProperties.arguments.webhook.parameters[parameterId].type = new Type(rawProperties.arguments.webhook.parameters[parameterId].type);
            }
        }
        return reactive(Array.isArray(rawProperties) ? {} : rawProperties);
    }

    property(name, defaultValue = null) {
        try {
            let path = this.properties;

            if (!name) {
                return path;
            }

            name.split('.').forEach((part) => {
                path = path.hasOwnProperty(part) && !Array.isArray(path[part]) ? path[part] : path[part] = reactive({});
            });

            return typeof path === 'undefined' ? defaultValue : path;
        } catch (ex) {
            return defaultValue;
        }
    }

    get arguments() {
        if (this.type === 'action') {
            return this.property(`arguments.${this.properties.id}`, {});
        }

        return [];
    }

    get outputs() {
        return NodeOutputsExtractor.hasOwnProperty(this.type) ? NodeOutputsExtractor[this.type](this) : [];
    }

    get errors() {
        return NodeErrorsChecker.hasOwnProperty(this.type) ? NodeErrorsChecker[this.type](this) : NodeErrorsBag.create();
    }

    get webhook_url() {
        return config.url.webhookBaseUrl.replace('SLUG',
            this.property('arguments.webhook').slug || this.property('arguments.webhook').uid);
    }

    get type_label() {
        return NODE_TYPES[this.type]?.label;
    }

    get type_icon() {
        return NODE_TYPES[this.type]?.icon;
    }

    get trigger_type_label() {
        return TRIGGER_TYPES[this.properties.type]?.label || 'N/A';
    }

    get trigger_type_icon() {
        return TRIGGER_TYPES[this.properties.type]?.icon || 'settings';
    }

    get tree_component() {
        switch (this.type) {
            case 'trigger':
                return TriggerNode.name;
            case 'action':
                return ActionNode.name;
            case 'assign':
                return AssignNode.name;
            case 'condition':
                return ConditionNode.name;
            case 'branch':
                return ConditionBranchNode.name;
        }

        return EmptyNode.name;
    }

    get settings_component() {
        switch (this.type) {
            case 'trigger':
                return TriggerSettings.name;
            case 'action':
                return ActionSettings.name;
            case 'assign':
                return AssignSettings.name;
            case 'condition':
                return ConditionSettings.name;
            case 'branch':
                return ConditionBranchSettings.name;
        }

        return EmptyNode.name;
    }

    get outputs_component() {
        return OutputsViewer.name;
    }

    get errors_component() {
        return ErrorsViewer.name;
    }

    get action() {
        return config.actions[this.properties.id];
    }

    get action_component() {
        if (!this.properties.id) {
            return EmptyNode.name;
        }

        if (!ACTION_COMPONENTS_MAP.hasOwnProperty(this.properties.id)) {
            ACTION_COMPONENTS_MAP[this.properties.id] = shallowRef(defineComponent({
                name: this.properties.id,
                template: config.actions[this.properties.id]?.template || `<div class="alert p-2 alert-danger small"><strong>${this.properties.id}</strong> action could not be found, please make sure that integrations are activated.</div>`,
                props: {
                    node: {type: Object, required: true},
                    arguments: {type: Object, required: false},
                },
                setup(props) {
                    const argument = (name, initialValue = null) => {
                        if (!props.arguments.hasOwnProperty(name)) {
                            props.arguments[name] = initialValue;
                        }

                        return props.arguments[name];
                    };

                    return {argument};
                }
            }));
        }

        return ACTION_COMPONENTS_MAP[this.properties.id];
    }

    get event_component() {
        if (!this.properties.arguments.event.id) {
            return EmptyNode.name;
        }

        if (!EVENT_COMPONENTS_MAP.hasOwnProperty(this.properties.arguments.event.id)) {
            EVENT_COMPONENTS_MAP[this.properties.arguments.event.id] = shallowRef(defineComponent({
                name: this.properties.arguments.event.id,
                template: config.triggers.events[this.properties.arguments.event.id]?.template || `<div></div>`,
                props: {
                    node: {type: Object, required: true},
                    arguments: {type: Object, required: false},
                },
                setup(props) {
                    const argument = (name, initialValue = null) => {
                        if (!props.arguments.hasOwnProperty(name)) {
                            props.arguments[name] = initialValue;
                        }

                        return props.arguments[name];
                    };

                    return {argument};
                }
            }));
        }

        return EVENT_COMPONENTS_MAP[this.properties.arguments.event.id];
    }
}
